// Telemetry sink (IMPLEMENTATION §2.9 "Telemetry"): a GameController observer that turns commands, SimEvents
// and state samples into telemetry rows. It sees every tick and every command, silent advances included.
//   unlock            subject = unlock id, value = its index in defs.unlocks (milestone times: the tick column)
//   goal              subject = the goal unlock id, value = its index (the goal chip moved)
//   discover          subject = "fam:tier", value = tier
//   purchase          subject = shop item id (a shop drop) or "chute" (a paint stroke), value = $ spent,
//                     extra = {to, outcome, n}
//   expand            subject = expansion id, value = $ spent
//   hint_shown        subject = tip id, value = 0; hint_done: subject = tip id, value = seconds it was shown
//   merge_all         value = merges
//   debug             subject = dbg op, value = its `a` (marks a session as debug-assisted)
//   stall_sec         per period: subject = stall id, value = seconds announced stalls lasted, extra = type
//   gallery_mode_sec  per period: subject = practice | firing | starved | gunLimited | noGun, value = seconds
//                     (sampled once per sim second, summed over Galleries)
// Durations are SIM time (ticks ÷ tickHz). The stall / Gallery totals are emitted every `periodSec` of sim
// time and by `closePeriod()` (the app calls it before a telemetry flush on hidden / pagehide).
import type { SimObserver } from '../game/observer.ts';
import type { CmdResult, Command } from '../sim/commands.ts';
import type { SimEvent } from '../sim/events.ts';
import { galleryStatus, shownStall } from '../sim/query.ts';
import type { Simulation } from '../sim/Simulation.ts';
import type { Telemetry } from './telemetry.ts';
import { errorRow, extraJson } from './telemetryRows.ts';

export interface TelemetrySinkConfig {
  /** Aggregation period of stall_sec / gallery_mode_sec in sim seconds (ui.json debug.telemetryPeriodSec). */
  periodSec: number;
}

export interface TelemetrySink extends SimObserver {
  /** Emits the open stall / Gallery totals now and starts a new period (before a flush on hide). */
  closePeriod(): void;
}

interface OpenStall {
  bid: number;
  stall: string;
  type: string;
  from: number;
}

export function createTelemetrySink(tel: Telemetry, cfg: TelemetrySinkConfig): TelemetrySink {
  let cur: Simulation | null = null;
  let periodStart = 0;
  let lastSample = 0;
  let open: OpenStall[] = [];
  let stallAcc = new Map<string, number>(); // "stall|type" → ticks
  let modes = new Map<number, string>(); // gallery id → mode at the last sample
  let modeAcc = new Map<string, number>(); // mode → gallery-ticks
  let hints = new Map<string, number>(); // tip id → tick shown
  let failed = false;

  const guard = (fn: () => void): void => {
    try {
      fn();
    } catch (err) {
      if (failed) return; // one row per session is enough
      failed = true;
      tel.log(errorRow('telemetry', err instanceof Error ? err.message : String(err)));
    }
  };

  const add = (m: Map<string, number>, k: string, n: number): void => {
    if (n > 0) m.set(k, (m.get(k) ?? 0) + n);
  };

  const modeOf = (sim: Simulation, bid: number, manual: boolean): string =>
    manual ? 'practice' : (galleryStatus(sim.state, sim.defs, bid)?.binding ?? 'noGun');

  /** Attributes the ticks since the last sample to each Gallery's last mode, then reads the modes now. */
  const sample = (sim: Simulation): void => {
    const now = sim.state.tick;
    const dt = now - lastSample;
    if (dt > 0) for (const m of modes.values()) add(modeAcc, m, dt);
    const next = new Map<number, string>();
    for (const b of sim.state.buildings) {
      if (b.type === 'gallery' && b.ghost === null) next.set(b.id, modeOf(sim, b.id, b.manual));
    }
    modes = next;
    lastSample = now;
  };

  const closeStall = (i: number, now: number): void => {
    const o = open[i] as OpenStall;
    add(stallAcc, `${o.stall}|${o.type}`, now - o.from);
    open.splice(i, 1);
  };

  const emitPeriod = (sim: Simulation): void => {
    const now = sim.state.tick;
    const hz = sim.defs.tickHz;
    sample(sim);
    for (let i = open.length - 1; i >= 0; i--) {
      const o = open[i] as OpenStall;
      if (!sim.state.buildings.some((b) => b.id === o.bid && b.stall === o.stall))
        closeStall(i, now);
      else {
        add(stallAcc, `${o.stall}|${o.type}`, now - o.from);
        o.from = now;
      }
    }
    for (const [k, ticks] of [...stallAcc].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
      const [stall = '', type = ''] = k.split('|');
      tel.log({ event: 'stall_sec', subject: stall, value: ticks / hz, extra: type });
    }
    for (const [mode, ticks] of [...modeAcc].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
      tel.log({ event: 'gallery_mode_sec', subject: mode, value: ticks / hz, extra: '' });
    }
    stallAcc = new Map();
    modeAcc = new Map();
    periodStart = now;
  };

  const onEvent = (e: SimEvent, sim: Simulation): void => {
    const d = sim.defs;
    const tick = sim.state.tick;
    switch (e.e) {
      case 'unlock':
        tel.log({
          event: 'unlock',
          subject: e.id,
          value: d.unlocks.findIndex((u) => u.id === e.id),
          extra: '',
        });
        break;
      case 'goal':
        tel.log({ event: 'goal', subject: d.unlocks[e.goal]?.id ?? '', value: e.goal, extra: '' });
        break;
      case 'discover':
        tel.log({ event: 'discover', subject: `${e.fam}:${e.tier}`, value: e.tier, extra: '' });
        break;
      case 'hint':
        if (e.done) {
          const at = hints.get(e.id);
          hints.delete(e.id);
          tel.log({
            event: 'hint_done',
            subject: e.id,
            value: at === undefined ? 0 : (tick - at) / d.tickHz,
            extra: '',
          });
        } else {
          hints.set(e.id, tick);
          tel.log({ event: 'hint_shown', subject: e.id, value: 0, extra: '' });
        }
        break;
      case 'mergeAll':
        tel.log({ event: 'merge_all', subject: '', value: e.n, extra: '' });
        break;
      case 'stall': {
        const b = sim.state.buildings.find((x) => x.id === e.bid);
        open.push({ bid: e.bid, stall: e.stall, type: b?.type ?? '', from: tick });
        break;
      }
      case 'unstall': {
        const i = open.findIndex((o) => o.bid === e.bid && o.stall === e.stall);
        if (i >= 0) closeStall(i, tick);
        break;
      }
      case 'bremove': {
        // sold / cancelled / undone: its stall ends with it (no unstall event)
        for (let i = open.length - 1; i >= 0; i--) if (open[i]?.bid === e.bid) closeStall(i, tick);
        break;
      }
      default:
        break;
    }
  };

  const onCommand = (
    cmd: Command,
    res: CmdResult,
    cashBefore: number,
    evs: readonly SimEvent[],
    sim: Simulation,
  ): void => {
    if (!res.ok) return;
    const spent = cashBefore - sim.state.wallet.cash;
    if (cmd.c === 'drop' && cmd.from.k === 'shop') {
      const placed = evs.find((e) => e.e === 'place');
      tel.log({
        event: 'purchase',
        subject: cmd.from.item,
        value: spent,
        extra: extraJson({
          to: cmd.to.k === 'cell' ? `${cmd.to.x},${cmd.to.y}` : cmd.to.k,
          outcome: placed ? (placed.e === 'place' && placed.ghost ? 'ghost' : 'place') : 'merge',
        }),
      });
    } else if (cmd.c === 'paintChutes' && spent > 0) {
      const n = evs.filter((e) => e.e === 'place').length;
      tel.log({ event: 'purchase', subject: 'chute', value: spent, extra: extraJson({ n }) });
    } else if (cmd.c === 'expand') {
      tel.log({ event: 'expand', subject: cmd.id, value: spent, extra: '' });
    } else if (cmd.c === 'dbg') {
      tel.log({ event: 'debug', subject: cmd.op, value: cmd.a ?? 0, extra: '' });
    }
  };

  return {
    onSim(sim) {
      guard(() => {
        if (cur) emitPeriod(cur); // the old game's open totals (a save load, loadSim, data reload)
        cur = sim;
        open = [];
        stallAcc = new Map();
        modes = new Map();
        modeAcc = new Map();
        hints = new Map();
        periodStart = sim.state.tick;
        lastSample = sim.state.tick;
        // stalls already announced in the start state (a save load, loadSim, data reload): the sim sends no
        // second `stall` event for them, so they are counted from this sim's start until unstall / bremove
        for (const b of sim.state.buildings) {
          const st = shownStall(sim.defs, b);
          if (st !== null) open.push({ bid: b.id, stall: st, type: b.type, from: sim.state.tick });
        }
        sample(sim);
      });
    },
    onCommand(cmd, res, cashBefore, evs, sim) {
      guard(() => onCommand(cmd, res, cashBefore, evs, sim));
    },
    onEvents(evs, sim) {
      guard(() => {
        for (const e of evs) onEvent(e, sim);
        const hz = sim.defs.tickHz;
        const now = sim.state.tick;
        if (now - lastSample >= hz) sample(sim);
        if (now - periodStart >= cfg.periodSec * hz) emitPeriod(sim);
      });
    },
    closePeriod() {
      guard(() => {
        if (cur) emitPeriod(cur);
      });
    },
  };
}
